import React, { Component } from 'react';
import { connect } from 'react-redux';

import screeningRemote from '../../api/screeningRemote';

import { receiveError } from '../../store/actions/notificationAction';

import ApiResponse from '../../models/ApiResponse';
import ResponseError from '../../models/ResponseError';
import Paper from '@material-ui/core/Paper';
import moment from 'moment';
import { TablePagination } from '@material-ui/core';

import User from '../../models/User';
import screeningStyles from './screeningReport.module.css';
import commonStyles from '../common/common.module.css';
import ScreeningReportFilterComponent from './ScreeningReportFilter';
import ScreeningReportTable from './ScreeningReportTable';
import Spinner from '../spinner/Spinner';

import ScreeningReport from '../../models/ScreeningReport';
import ScreeningReportItem from '../../models/screeningReportItem';
import { baseUrl } from '../../api/baseUrl';
import { ExportToCsv } from 'export-to-csv';
import { screeningMeasures } from '../../utils/screening';


interface ScreeningReportContainerProps {
  receiveError: (error: ResponseError) => void;
  user: User;
  buttonState: boolean;
};

interface ScreeningReportContainerState {
  awb: Array<ScreeningReport>;
  filteredReport: Array<ScreeningReport>;
  airlines: Array<string>;
  flightFilter: Array<string>;
  airlinesVal: string;
  flightVal: string;
  bookedFlightFilter: Array<string>;
  bookedFlightVal: string;
  view: string;
  fromDate: Date;
  fromTime: Date;
  toDate: Date;
  toTime: Date;
  page: number;
  rowsPerPage: number;
  count: number;
  keyword: string;
  selectedMeasures: Array<string>;
  listMeasure: Array<string>;
  loading: boolean;
};

class ScreeningReportContainer extends Component<ScreeningReportContainerProps, ScreeningReportContainerState> {
  state: ScreeningReportContainerState = {
    awb: [],
    filteredReport: [],
    flightFilter: [],
    airlines: [],
    airlinesVal: '',
    flightVal: '',
    bookedFlightFilter: [],
    bookedFlightVal: '',
    view: 'render',
    fromDate: new Date(),
    fromTime: new Date(new Date().setHours(0, 0, 0, 0)),
    toDate: new Date(),
    toTime: new Date(new Date().setHours(23, 59, 0, 0)),
    page: 0,
    rowsPerPage: 10,
    count: 0,
    keyword: '',
    selectedMeasures: [],
    listMeasure: [],
    loading: false
  };

  private stopLoading = (): void => {
    this.setState({ loading: false });
  }

  private startLoading = (): void => {
    this.setState({ loading: true });
  }

  private static REPORT_CACHE_TTL_MS = 2 * 60 * 1000;
  private static REPORT_CACHE_MAX = 8;
  private reportCache: Map<string, { expires: number; data: Array<ScreeningReport> }> = new Map();

  private buildReportCacheKey = (
    fromDate: string,
    fromTime: string,
    toDate: string,
    toTime: string,
    keyword: string,
    measures: Array<string>
  ): string => {
    const measuresKey = (measures || []).slice().map(String).sort().join(',');
    return [fromDate, fromTime, toDate, toTime, keyword || '', measuresKey].join('|');
  }

  private readReportCache = (key: string): Array<ScreeningReport> | null => {
    const entry = this.reportCache.get(key);
    if (!entry) {
      return null;
    }
    if (Date.now() > entry.expires) {
      this.reportCache.delete(key);
      return null;
    }
    return entry.data;
  }

  private writeReportCache = (key: string, data: Array<ScreeningReport>): void => {
    if (this.reportCache.size >= ScreeningReportContainer.REPORT_CACHE_MAX) {
      const oldestKey = this.reportCache.keys().next().value;
      if (oldestKey) {
        this.reportCache.delete(oldestKey);
      }
    }
    this.reportCache.set(key, {
      expires: Date.now() + ScreeningReportContainer.REPORT_CACHE_TTL_MS,
      data
    });
  }

  private clearReportCache = (): void => {
    this.reportCache.clear();
  }

  private applyReportResult = (result: Array<ScreeningReport>): void => {
    let flights: Array<string> = [];
    let bkdFlights: Array<string> = [];
    let airlines: Array<string> = [];
    result.forEach((item: ScreeningReport) => {
      (item.screening || []).forEach((screening: ScreeningReportItem) => {
        if (screening && screening.flight && !flights.includes(screening.flight)) {
          flights.push(screening.flight);
        }
      });
      const airlineCode = this.normalizeAirline(item.flight);
      if (airlineCode && !airlines.includes(airlineCode)) {
        airlines.push(airlineCode);
      }
      const bookedLabel = this.getBookedFlightLabel(item);
      if (bookedLabel && !bkdFlights.includes(bookedLabel)) {
        bkdFlights.push(bookedLabel);
      }
    });
    airlines.sort();
    flights.sort();
    bkdFlights.sort();

    // Keep active dropdown filters if they still exist in the new result set
    const airlinesVal = airlines.includes(this.state.airlinesVal) ? this.state.airlinesVal : '';
    const flightVal = flights.includes(this.state.flightVal) ? this.state.flightVal : '';
    const bookedFlightVal = bkdFlights.includes(this.state.bookedFlightVal) ? this.state.bookedFlightVal : '';
    const filteredReport = this.applyClientFilters(result, {
      airline: airlinesVal,
      bookedFlight: bookedFlightVal,
      screenedFlight: flightVal
    });

    this.setState({
      flightVal,
      airlinesVal,
      bookedFlightVal,
      awb: result,
      filteredReport,
      count: filteredReport.length,
      airlines,
      flightFilter: flights,
      bookedFlightFilter: bkdFlights,
      page: 0,
      loading: false
    });
  }

  private filterByScreenedFlight = (rows: Array<ScreeningReport>, flight: string): Array<ScreeningReport> => {
    const target = (flight || '').toString().trim();
    if (!target) {
      return rows;
    }
    return rows
      .map((item: ScreeningReport) => {
        const screening = (item.screening || []).filter((s: ScreeningReportItem) =>
          s && (s.flight || '').toString().trim() === target
        );
        if (!screening.length) {
          return null;
        }
        return { ...item, screening };
      })
      .filter(Boolean) as Array<ScreeningReport>;
  }

  private applyClientFilters = (
    source: Array<ScreeningReport>,
    filters: { airline?: string; bookedFlight?: string; screenedFlight?: string }
  ): Array<ScreeningReport> => {
    let rows = source || [];
    if (filters.airline) {
      rows = rows.filter((item: ScreeningReport) => this.matchesAirline(item, filters.airline as string));
    }
    if (filters.bookedFlight) {
      rows = rows.filter((item: ScreeningReport) => this.matchesBookedFlight(item, filters.bookedFlight as string));
    }
    if (filters.screenedFlight) {
      rows = this.filterByScreenedFlight(rows, filters.screenedFlight);
    }
    return rows;
  }

  private updateClientFilters = (next: {
    airlinesVal?: string;
    bookedFlightVal?: string;
    flightVal?: string;
  }): void => {
    const airlinesVal = next.airlinesVal !== undefined ? next.airlinesVal : this.state.airlinesVal;
    const bookedFlightVal = next.bookedFlightVal !== undefined ? next.bookedFlightVal : this.state.bookedFlightVal;
    const flightVal = next.flightVal !== undefined ? next.flightVal : this.state.flightVal;
    const filteredReport = this.applyClientFilters(this.state.awb, {
      airline: airlinesVal,
      bookedFlight: bookedFlightVal,
      screenedFlight: flightVal
    });
    this.setState({
      airlinesVal,
      bookedFlightVal,
      flightVal,
      filteredReport,
      count: filteredReport.length,
      page: 0
    });
  }

  componentDidMount() {
    this.getFilterMeasures();
  }

  print = (): void => {
    const params = new URLSearchParams();
    params.set('measures', JSON.stringify(this.state.selectedMeasures || []));

    const fromDate: string = moment(this.state.fromDate).format('YYYYMMDD');
    const toDate: string = moment(this.state.toDate).format('YYYYMMDD');
    const fromTime: string = moment(this.state.fromTime).format('HH:mm:ss');
    const toTime: string = moment(this.state.toTime).format('HH:mm:ss');

    if (fromDate) {
      params.set('start_date', fromDate);
    }
    if (toDate) {
      params.set('end_date', toDate);
    }
    if (fromTime) {
      params.set('start_time', fromTime);
    }
    if (toTime) {
      params.set('end_time', toTime);
    }
    if (this.state.airlinesVal) {
      params.set('airline', this.state.airlinesVal);
    }
    if (this.state.flightVal) {
      params.set('scrFlight', this.state.flightVal);
    }
    if (this.state.bookedFlightVal) {
      params.set('bkdFlight', this.state.bookedFlightVal);
    }
    if (this.state.keyword) {
      params.set('keyword', this.state.keyword);
    }

    window.open(`${baseUrl}screening/file?${params.toString()}`);
  }

  private MAX_REPORT_DAYS = 31;

  private isNonProductionEnv = (): boolean => {
    const stage = (process.env.REACT_APP_STAGE || 'local').toLowerCase();
    return stage === 'local' || stage === 'dev' || process.env.NODE_ENV === 'development';
  }

  private validateDateRange = (): boolean => {
    if (!moment(this.state.fromDate).isValid() || !moment(this.state.toDate).isValid()) {
      this.props.receiveError({ errorCode: 'InvalidDate', message: 'Please select a valid date range' } as any);
      return false;
    }
    const start = moment(this.state.fromDate).startOf('day');
    const end = moment(this.state.toDate).startOf('day');
    if (end.isBefore(start)) {
      this.props.receiveError({ errorCode: 'InvalidDate', message: 'End date must be on or after start date' } as any);
      return false;
    }
    // No max-range enforcement on local/dev
    if (!this.isNonProductionEnv() && end.diff(start, 'days') > this.MAX_REPORT_DAYS) {
      this.props.receiveError({
        errorCode: 'DateRangeTooLarge',
        message: `Screening report date range cannot exceed ${this.MAX_REPORT_DAYS} days. Please narrow the range.`
      } as any);
      return false;
    }
    return true;
  }

  private normalizeAirline = (value?: string | null): string => {
    return (value || '').toString().trim().toUpperCase();
  }

  private matchesAirline = (item: ScreeningReport, airline: string): boolean => {
    return this.normalizeAirline(item.flight) === this.normalizeAirline(airline);
  }

  private getBookedFlightLabel = (item: ScreeningReport): string => {
    const meta = item && item.export_metadata;
    if (!meta || !meta.firstFlightNumber) {
      return '';
    }
    const datePart = meta.firstFlightDate ? moment(meta.firstFlightDate).format('MMM DD') : '';
    return `${meta.firstFlightNumber}/${datePart}`;
  }

  private matchesBookedFlight = (item: ScreeningReport, bookedFlight: string): boolean => {
    return this.getBookedFlightLabel(item) === bookedFlight;
  }

  handleAirlineChange = (event: any): void => {
    const val: string = event.target.value || '';
    this.updateClientFilters({ airlinesVal: val });
  }

  fromDateChange = (date: Date): void => {
    this.setState({ fromDate: date });
  }

  toDateChange = (date: Date): void => {
    this.setState({ toDate: date });
  }

  fromTimeChange = (date: Date): void => {
    this.setState({ fromTime: date });
  }

  toTimeChange = (date: Date): void => {
    this.setState({ toTime: date });
  }

  handleFlightChange = (event: any, type: string): void => {
    const val: string = (event.target.value || '').toString();
    if (type === 'bkd') {
      this.updateClientFilters({ bookedFlightVal: val });
    } else {
      this.updateClientFilters({ flightVal: val });
    }
  }

  handleChangeRowsPerPage = (event: any): void => {
    const rowsPerPage: number = event.target.value;
    // Pagination is client-side over filteredReport — do not refetch or reset filters
    this.setState({ rowsPerPage, page: 0 });
  }

  private mapCsvRow = (row: any): any => {
    return {
      AWB: row.awb,
      Airline: row.iata_code || row.flight || '',
      Pieces: row.pieces,
      Weight: row.weight,
      Origin: row.org,
      Destination: row.dst,
      'Drop Off Date': row.drop_off_date,
      'Drop Off Time': row.drop_off_time,
      Forwarder: row.forwarder_name,
      Screener: row.screened_by,
      'Screening Date': row.date_time,
      'Screening Time': row.time,
      'Actual Weight': row.actual_weight,
      'Measure Applied': row.measure_applied,
      'Screening Details': row.screening_details,
      'For Flight': row.flight,
      Alarm: row.alarm,
      Resolution: row.alarm_resolution,
      Details: row.details
    };
  }

  downloadCsv = (): void => {
    if (this.state.loading) {
      return;
    }
    if (!this.state.keyword && !this.validateDateRange()) {
      return;
    }

    const fromDate: string = moment(this.state.fromDate).format('YYYYMMDD');
    const toDate: string = moment(this.state.toDate).format('YYYYMMDD');
    const fromTime: string = moment(this.state.fromTime).format('HH:mm:ss');
    const toTime: string = moment(this.state.toTime).format('HH:mm:ss');
    this.startLoading();
    screeningRemote.getScreeningCsv(
      fromDate,
      fromTime,
      toDate,
      toTime,
      this.state.keyword,
      this.state.flightVal,
      this.state.airlinesVal
    ).then((res: ApiResponse<Array<any>>) => {
      const result: Array<any> | undefined = res.result;
      const error: ResponseError | undefined = res.error;
      if(result) {
        const options = {
          fieldSeparator: ',',
          quoteStrings: '"',
          decimalSeparator: '.',
          showLabels: true,
          showTitle: true,
          title: 'Screening Report CSV',
          useTextFile: false,
          useBom: true,
          useKeysAsHeaders: true,
        };
        if(result.length > 0) {
          let filteredTable: Array<any> = result;

          if (this.state.airlinesVal) {
            const airline = this.normalizeAirline(this.state.airlinesVal);
            filteredTable = filteredTable.filter((item: any) =>
              this.normalizeAirline(item.iata_code || item.flight) === airline
            );
          }

          if (this.state.bookedFlightVal) {
            filteredTable = filteredTable.filter((item: any) => {
              const meta = item.export_metadata || {};
              if (!meta.firstFlightNumber) {
                return false;
              }
              const label = `${meta.firstFlightNumber}/${meta.firstFlightDate ? moment(meta.firstFlightDate).format('MMM DD') : ''}`;
              return label === this.state.bookedFlightVal;
            });
          }

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(filteredTable.map(this.mapCsvRow));
        }
      }
      if (error) {
        this.props.receiveError(error);
      }
    }).catch(() => {
      this.props.receiveError({
        errorCode: 'ScreeningCsvError',
        message: 'Failed to export screening report CSV. Please try again.'
      } as any);
    }).then(() => {
      this.stopLoading();
    });
  }

  handleChangePage = (event: any, newPage: number): void => {
    if (event) {
      this.setState({ page: newPage });
    } else {
      return;
    }
  }

  handleChangeMeasures = (event: any ): void => {

    const values = event.target.value;
    let selectedMeasures = this.state.selectedMeasures;

    selectedMeasures = values;
    this.setState({ selectedMeasures });
  };

  getFilterMeasures = (): void => {
    this.startLoading();
    screeningRemote.getScreeningMeasures().then((res: ApiResponse<Array<string>>) => {
      const result: Array<string> | undefined = res.result;
      const error: ResponseError | undefined = res.error;

      if (result) {
        this.setState({
         listMeasure: result,
         selectedMeasures: result
        }, () => {
          this.getScreeningReport(undefined, true);
        });
        return;
      }

      this.stopLoading();
      if (error) {
        this.props.receiveError(error);
      }
    }).catch(() => {
      this.stopLoading();
      this.props.receiveError({
        errorCode: 'ScreeningMeasuresError',
        message: 'Failed to load screening measures. Please try again.'
      } as any);
    });
  }

  handleChangeKeyword = (event: any): void => {
    let value: string = event.target.value.toString();
    if (value.length === 3 && this.state.keyword.length === 2) {
      value = value + "-";
    }
    if (value.length === 11) {
      if (value.indexOf("-") < 0) {
        value = value.slice(0, 3) + "-" + value.slice(3);
      }
    }
    if (value.split("-").length > 2) {
      return;
    }
    this.setState({ keyword: value });
  }

  getReport = () => {
    this.getScreeningReport();
  }

  clear = (): void => {
    if (this.state.loading) {
      return;
    }
    this.clearReportCache();
    this.setState({
      filteredReport: [],
      awb: [],
      flightFilter: [],
      airlines: [],
      airlinesVal: '',
      flightVal: '',
      bookedFlightFilter: [],
      bookedFlightVal: '',
      fromDate: new Date(),
      fromTime: new Date(new Date().setHours(0, 0, 0, 0)),
      toDate: new Date(),
      toTime: new Date(new Date().setHours(23, 59, 0, 0)),
      keyword: ''
    });
  }

  getScreeningReport = (flight?: string, continueLoading?: boolean): void => {
    if (this.state.loading && !continueLoading) {
      return;
    }
    if (!this.state.keyword && !this.validateDateRange()) {
      if (continueLoading) {
        this.stopLoading();
      }
      return;
    }

    // Legacy screened-flight arg: filter locally, no network
    if (flight) {
      this.updateClientFilters({ flightVal: flight });
      this.stopLoading();
      return;
    }

    const fromDate: string = moment(this.state.fromDate).format('YYYYMMDD');
    const toDate: string = moment(this.state.toDate).format('YYYYMMDD');
    const fromTime: string = moment(this.state.fromTime).format('HH:mm:ss');
    const toTime: string = moment(this.state.toTime).format('HH:mm:ss');
    const cacheKey = this.buildReportCacheKey(
      fromDate,
      fromTime,
      toDate,
      toTime,
      this.state.keyword,
      this.state.selectedMeasures
    );
    const cached = this.readReportCache(cacheKey);
    if (cached) {
      try {
        this.applyReportResult(cached);
      } catch (e) {
        this.stopLoading();
        this.props.receiveError({
          errorCode: 'ScreeningReportError',
          message: 'Failed to process cached screening report. Please try again.'
        } as any);
      }
      return;
    }

    if (!continueLoading) {
      this.startLoading();
    }

    screeningRemote.getScreeningReport(fromDate, fromTime, toDate, toTime, this.state.keyword, this.state.selectedMeasures).then((res: ApiResponse<Array<ScreeningReport>>) => {
      const result: Array<ScreeningReport> | undefined = res.result;
      const error: ResponseError | undefined = res.error;

      if (result) {
        try {
          this.writeReportCache(cacheKey, result);
          this.applyReportResult(result);
        } catch (e) {
          this.stopLoading();
          this.props.receiveError({
            errorCode: 'ScreeningReportError',
            message: 'Failed to process screening report data. Please try again.'
          } as any);
        }
      } else {
        this.stopLoading();
      }

      if (error) {
        this.props.receiveError(error);
      }
    }).catch(() => {
      this.stopLoading();
      this.props.receiveError({
        errorCode: 'ScreeningReportError',
        message: 'Failed to load screening report. Please narrow the date range and try again.'
      } as any);
    });
  };

  render() {
    const { rowsPerPage, page, loading } = this.state;
    const searchDisabled = this.props.buttonState || loading;
    return (
      
      <div className={ screeningStyles.mainContainer }>
        { loading && <Spinner /> }
        <div className= { commonStyles.headerDummyDiv }></div>
        <ScreeningReportFilterComponent
          fromDate={ this.state.fromDate }
          bookedFlightFilter={ this.state.bookedFlightFilter }
          bookedFlightVal={ this.state.bookedFlightVal }
          fromTime={ this.state.fromTime }
          toDate={ this.state.toDate }
          toTime={ this.state.toTime }
          airlinesVal={ this.state.airlinesVal }
          flightVal={ this.state.flightVal }
          handleAirlineChange={ this.handleAirlineChange }
          handleFlightChange={ this.handleFlightChange }
          getScreeningReport={ this.getReport }
          print={ this.print }
          flightFilter={ this.state.flightFilter }
          airlines={ this.state.airlines }
          fromDateChange={ this.fromDateChange }
          fromTimeChange={ this.fromTimeChange }
          toDateChange={ this.toDateChange }
          toTimeChange={ this.toTimeChange }
          handleChangeKeyword={ this.handleChangeKeyword }
          awbNumber={ this.state.keyword }
          clear={ this.clear }
          csv={ this.downloadCsv }
          selectedMeasures={ this.state.selectedMeasures }
          listMeasure={ this.state.listMeasure }
          handleChangeMeasureFilter={ this.handleChangeMeasures }
          buttonState={ searchDisabled }
          loading={ loading }
        />
        <Paper className={ screeningStyles.root }>
          <ScreeningReportTable
            filteredReport={ this.state.filteredReport }
            page={ this.state.page }
            rowsPerPage={ this.state.rowsPerPage }
            count={ this.state.count }
          />
          {this.state.view === 'render' && this.state.filteredReport.length > 0 && (
            <TablePagination
              rowsPerPageOptions={ [10, 15, 25] }
              component="div"
              count={ this.state.count }
              rowsPerPage={ rowsPerPage }
              page={ page } 
              backIconButtonProps={{
                'aria-label': 'Previous Page',
              }}
              nextIconButtonProps={{
                'aria-label': 'Next Page',
              }}
              onPageChange={ this.handleChangePage }
              onChangeRowsPerPage={ (e) => this.handleChangeRowsPerPage(e) }
            />
          )}
        </Paper>
      </div>

    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    buttonState: state.buttonState
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    receiveError: (error: ResponseError) => dispatch(receiveError(error)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ScreeningReportContainer);
